import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AnalyticsRoutes } from "@/components/AnalyticsRoutes";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RequireAuth } from "@/components/RequireAuth";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import { AuthProvider } from "@/lib/auth";
import { ConfigProvider } from "@/lib/config";
import { ProgressProvider } from "@/lib/progress";
import WeekMilestone from "@/pages/WeekMilestone";
import Completion from "@/pages/Completion";
import DayPage from "@/pages/DayPage";
import DaysIndex from "@/pages/DaysIndex";
import GuidePage from "@/pages/GuidePage";
import IntroPage from "@/pages/IntroPage";
import KitPreview from "@/pages/KitPreview";
import Library from "@/pages/Library";
import NotFound from "@/pages/NotFound";
import OperatorConsole from "@/pages/OperatorConsole";
import OpenLink from "@/pages/OpenLink";
import ProgramHome from "@/pages/ProgramHome";
import Profile from "@/pages/Profile";
import { track } from "@/lib/analytics";

// Anything that escapes a component's own handling still gets reported once.
window.addEventListener("error", (e) => {
  if (!e.message) return;
  track("app_error", {
    error_message: String(e.message).slice(0, 200),
    error_source: "window",
    page_path: window.location.pathname,
  });
});
window.addEventListener("unhandledrejection", (e) => {
  track("app_error", {
    error_message: String(e.reason?.message ?? e.reason ?? "unhandled rejection").slice(0, 200),
    error_source: "promise",
    page_path: window.location.pathname,
  });
});

const Private = ({ children }) => <RequireAuth>{children}</RequireAuth>;

/** Every screen inside a program shares one progress store, so rings, completed days and resume
 *  stay in step as the reader ticks meals — no refresh, no stale numbers. */
const InProgram = ({ children }) => {
  const { slug } = useParams();
  return (
    <RequireAuth>
      <ProgressProvider slug={slug}>{children}</ProgressProvider>
    </RequireAuth>
  );
};

export default function App() {
  return (
    <ConfigProvider>
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <AnalyticsRoutes />
          <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Navigate to="/library" replace />} />
            <Route path="/open" element={<OpenLink />} />
            <Route path="/design-system" element={<KitPreview />} />

            <Route path="/library" element={<Private><Library /></Private>} />
            {/* Operator Console: the central place for Recipe Sync. Gated server-side; the page
                itself also checks the capability, so a customer never sees operator chrome. */}
            <Route path="/operator" element={<Private><OperatorConsole /></Private>} />
            <Route path="/profile" element={<Private><Profile /></Private>} />
            <Route path="/p/:slug" element={<InProgram><ProgramHome /></InProgram>} />
            <Route path="/p/:slug/intro/:page" element={<Private><IntroPage /></Private>} />
            <Route path="/p/:slug/days" element={<InProgram><DaysIndex /></InProgram>} />
            <Route path="/p/:slug/day/:dayNumber" element={<InProgram><DayPage /></InProgram>} />
            <Route path="/p/:slug/guide/:guideKey" element={<Private><GuidePage /></Private>} />
            <Route path="/p/:slug/week/:week" element={<InProgram><WeekMilestone /></InProgram>} />
            <Route path="/p/:slug/complete" element={<InProgram><Completion /></InProgram>} />

            <Route path="*" element={<NotFound />} />
          </Routes>
          </ErrorBoundary>
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </ConfigProvider>
  );
}
